import { ReactNode } from "react";
import {
  Root,
  Trigger,
  Content,
  DropdownMenuContentProps,
  DropdownMenuProps,
} from "@radix-ui/react-dropdown-menu";
import { cn } from "@/utils";
import { Check, ChevronLeft } from "@/icons";
import { Button } from "../Button";

const CONTENT_CLASSNAME =
  "z-[65] flex flex-col rounded-[16px] border border-border bg-surface-primary"; // Z Index must exceed the Drawer component to be safely used in a drawer

const COLORS: Record<string, string> = {
  default: "text-item-primary",
  error: "text-error-item",
};

const renderOptions = (
  options: DropdownOption[],
  optionsClassName?: string,
) => {
  return options.map(
    ({ text, icon, color, selected, onClick, loading, disabled }, idx) => {
      return (
        <Button
          key={text}
          onClick={onClick}
          icon={icon}
          iconPosition="left"
          data-testid={`option-${idx}`}
          variant={color === "error" ? "tertiaryDanger" : "tertiary"}
          size="s"
          className={cn("justify-start", optionsClassName)}
          loading={loading}
          disabled={disabled}
        >
          {text}
          {selected && (
            <Check className={cn("ml-1 h-4 w-4", COLORS[color ?? "default"])} />
          )}
        </Button>
      );
    },
  );
};

const HeaderOption = ({
  text,
  onClick,
}: {
  text: string;
  onClick: () => void;
}) => {
  return (
    <>
      <Button
        key={text}
        onClick={onClick}
        icon={<ChevronLeft className="h-4 w-4" />}
        iconPosition="left"
        variant="tertiary"
        size="s"
        className="mb-1"
      >
        {text}
      </Button>
      <div className="mb-1 h-[1px] w-full bg-border" />
    </>
  );
};

export interface DropdownOption {
  text: string;
  onClick: () => void;
  icon?: ReactNode;
  color?: string;
  selected?: boolean;
  loading?: boolean;
  disabled?: boolean;
}

interface DropdownProps extends DropdownMenuProps {
  options?: DropdownOption[];
  optionsGroups?: DropdownOption[][]; // Multiple groups of options separated by dividers
  customContent?: ReactNode; // If provided, will override options/optionsGroups
  children?: ReactNode;
  contentProps?: Partial<DropdownMenuContentProps>;
  dataTestId?: string;
  className?: string;
  optionsClassName?: string;
  asChild?: boolean;
  heading?: string;
  headerOption?: { text: string; onClick: () => void };
}

export const Dropdown = ({
  children,
  options,
  optionsGroups,
  contentProps,
  dataTestId,
  className,
  optionsClassName,
  asChild, // Pass trigger to child elements
  heading,
  headerOption,
  customContent,
  ...props
}: DropdownProps) => {
  const renderContent = () => {
    const baseContentProps = {
      "data-testid": dataTestId,
      ...contentProps,
      className: cn(CONTENT_CLASSNAME, className),
    };

    if (customContent) {
      return <Content {...baseContentProps}>{customContent}</Content>;
    }

    if (optionsGroups) {
      return (
        <Content {...baseContentProps}>
          {heading && (
            <div className="caption border-b border-border px-[22px] pb-4 pt-5 text-item-primary">
              {heading}
            </div>
          )}
          {optionsGroups.map((options, idx) => (
            <div
              key={idx}
              className={cn(
                "flex flex-col gap-1 p-3",
                idx !== optionsGroups.length - 1 && "border-b border-border",
              )}
            >
              {options && renderOptions(options, optionsClassName)}
            </div>
          ))}
        </Content>
      );
    }

    return (
      <Content
        {...baseContentProps}
        className={cn("gap-1 p-3", baseContentProps.className)}
      >
        {headerOption && <HeaderOption {...headerOption} />}
        {options && renderOptions(options, optionsClassName)}
      </Content>
    );
  };

  return (
    <Root {...props} modal={false}>
      <Trigger asChild={asChild} className="focus-visible:outline-none">
        {children}
      </Trigger>
      {renderContent()}
    </Root>
  );
};
